import TableBodyRow from './TableBodyRow';

import { getPinnedAwareVisibleLeafColumns } from '../../../domain/table/tableHeadUseCase';
import { getRowVirtualizerSpacerHeights } from '../../../domain/table/tableRowVirtualizerUseCase';

import styles from './TableBody.module.css';

/**
 * Table Body component
 * @param {object} params
 * @param {import('@tanstack/react-table').Row<TableRowFlattened>[]} [params.rows] - Rows to render (alternative to table)
 * @param {TableInstance} [params.table] - TanStack table (for merged cell widths)
 * @param {(params: { cell: TableCell }) => React.ReactNode} [params.children] - Optional render prop for a cell
 * @param {(groupId: string, id: string, options?: { shiftKey?: boolean }) => void} [params.handleSelectRow] - Handle select row
 * @param {import('../../../domain/table/tableReducer').SelectedRowsState} [params.selectedRows] - Selected rows state
 * @param {TableActiveCell | null} [params.activeCell] - Currently editing cell
 * @param {import('@tanstack/react-virtual').Virtualizer<HTMLDivElement, Element>} [params.rowVirtualizer] - Inner row virtualizer
 * @param {number} [params.rowVirtualizerScrollMargin=0] - scrollMargin used by the inner virtualizer
 * @param {string | null} [params.highlightedRowId] - Row id highlighted by in-table find
 * @returns {JSX.Element}
 */
function TableBody({
  rows,
  table,
  children,
  handleSelectRow,
  selectedRows,
  activeCell = null,
  rowVirtualizer = null,
  rowVirtualizerScrollMargin = 0,
  highlightedRowId = null,
}) {
  const bodyRows = rows || [];
  const tableWidth = table?.getTotalSize() ?? 0;
  const colCount = table ? Math.max(1, getPinnedAwareVisibleLeafColumns(table).length) : 1;

  const virtualItems = rowVirtualizer ? rowVirtualizer.getVirtualItems() : null;
  const isVirtualized = virtualItems !== null;
  const { paddingTop, paddingBottom } = isVirtualized
    ? getRowVirtualizerSpacerHeights(
      virtualItems,
      rowVirtualizer.getTotalSize(),
      rowVirtualizerScrollMargin,
    )
    : { paddingTop: 0, paddingBottom: 0 };

  /** @type {Array<{ row: import('@tanstack/react-table').Row<TableRowFlattened>, virtualIndex?: number }>} */
  const rowsToRender = isVirtualized
    ? virtualItems
      .map((virtualRow) => ({
        row: bodyRows[virtualRow.index],
        virtualIndex: virtualRow.index,
      }))
      .filter((item) => item.row)
    : bodyRows.map((row) => ({ row }));

  return (
    <tbody>
      {isVirtualized && paddingTop > 0 ? (
        <tr aria-hidden="true" className={styles['table-body-spacer-row']}>
          <td
            aria-hidden="true"
            colSpan={colCount}
            className={styles['table-body-spacer-cell']}
            style={{ height: `${paddingTop}px` }}
          />
        </tr>
      ) : null}
      {rowsToRender.map(({ row, virtualIndex }) => {
        const rowOriginal = row.original;
        const isSelected = selectedRows?.[rowOriginal.$groupId]?.[rowOriginal.id] ?? false;
        const activeColumnId = activeCell?.rowId === rowOriginal.id
          ? activeCell.columnId
          : null;

        return (
          <TableBodyRow
            key={rowOriginal.id}
            row={row}
            table={table}
            handleSelectRow={handleSelectRow}
            isSelected={isSelected}
            activeColumnId={activeColumnId}
            tableWidth={tableWidth}
            measureRef={isVirtualized ? rowVirtualizer.measureElement : undefined}
            virtualIndex={virtualIndex}
            isFindCurrent={highlightedRowId === rowOriginal.id}
          >
            {children}
          </TableBodyRow>
        );
      })}
      {isVirtualized && paddingBottom > 0 ? (
        <tr aria-hidden="true" className={styles['table-body-spacer-row']}>
          <td
            aria-hidden="true"
            colSpan={colCount}
            className={styles['table-body-spacer-cell']}
            style={{ height: `${paddingBottom}px` }}
          />
        </tr>
      ) : null}
    </tbody>
  );
}

export default TableBody;
